// PDP-only UX behaviors. Loaded via `defer` in theme.liquid only when request.page_type == 'product'.
// 2026-05-13 cleanup pass:
//   - removed ShareChat SDK (unused — only product-ux.js itself called scq)
//   - removed IE 11 branch (IE retired 2022-06)
//   - countdown timer: rAF (60Hz) → setInterval(1s) — same visual output, 60× less work
//   - sticky-cart scroll: cache element + RAF-throttle + early-return when bar absent
//   - event delegation for accordion + additional-info (no per-element listeners at boot)
//   - openCheckout moved to global.js (needs to exist on every page where cart-drawer renders)
//   - collection-card behaviors (variant pill swatch, brand A-Z) moved to collection-ux.js
(function () {
  'use strict';

  // -------- helpers ----------------------------------------------------------
  function safeIdleCallback(cb) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(cb, { timeout: 1000 });
    } else {
      setTimeout(cb, window.innerWidth < 768 ? 100 : 200);
    }
  }

  function domReady(cb) {
    if (document.readyState === 'complete' ||
        (document.readyState === 'interactive' && document.body)) {
      setTimeout(cb, 0);
    } else {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    }
  }

  // -------- product image slider (legacy `optimize-slider` markup) ----------
  // Used by snippets/product-media-gallery.liquid (= all default templates).
  // The `new-template` PDPs (pilepro/oligo/visvasa/crush/hindivisvasa) use the
  // separate `my-media-gallery.liquid` swiper — not this code.
  function initializeProductSlider() {
    const slider = document.querySelector('.product-image-slider');
    if (!slider) return;

    const track = slider.querySelector('.optimize-slider-track');
    const slides = track && track.querySelectorAll('.optimize-slide');
    const prevBtn = slider.querySelector('.optimize-prev');
    const nextBtn = slider.querySelector('.optimize-next');
    const dots = slider.querySelectorAll('.optimize-dot');
    const heroImage = document.querySelector('.optimize-main-product-image');

    if (!track || !slides || slides.length === 0) return;

    let currentIndex = 0;
    const totalSlides = slides.length;
    let isAnimating = false;

    track.style.willChange = 'transform';
    track.style.transform = 'translateZ(0)';

    slider.classList.remove('optimize-slider-hidden');
    if (heroImage) heroImage.style.display = 'none';

    slides.forEach((slide) => {
      slide.style.display = 'block';
      slide.style.willChange = 'transform';
    });

    function updateSlider() {
      if (isAnimating) return;
      isAnimating = true;
      requestAnimationFrame(() => {
        track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;
        dots.forEach((dot, idx) => {
          if (idx === currentIndex) dot.classList.add('active');
          else dot.classList.remove('active');
        });
        isAnimating = false;
      });
    }

    let navTimeout;
    function navigate(direction) {
      if (navTimeout) return;
      navTimeout = setTimeout(() => (navTimeout = null), 300);
      if (direction === 'next') {
        currentIndex = (currentIndex + 1) % totalSlides;
      } else {
        currentIndex = (currentIndex - 1 + totalSlides) % totalSlides;
      }
      updateSlider();
    }

    if (nextBtn) nextBtn.addEventListener('click', () => navigate('next'), { passive: true });
    if (prevBtn) prevBtn.addEventListener('click', () => navigate('prev'), { passive: true });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.getAttribute('data-index'), 10);
        if (!isNaN(index) && index !== currentIndex) {
          currentIndex = index;
          updateSlider();
        }
      }, { passive: true });
    });

    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping = false;
    const swipeThreshold = 50;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = true;
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;
      const diffX = Math.abs(touchStartX - e.touches[0].clientX);
      const diffY = Math.abs(touchStartY - e.touches[0].clientY);
      if (diffX > diffY && diffX > 10) e.preventDefault();
    }, { passive: false });

    track.addEventListener('touchend', (e) => {
      if (!isSwiping) return;
      const diffX = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diffX) > swipeThreshold) navigate(diffX > 0 ? 'next' : 'prev');
      isSwiping = false;
    }, { passive: true });

    updateSlider();
  }

  // -------- countdown timer --------------------------------------------------
  function initCountdown() {
    const timerElement = document.querySelector('#bs_custom_bs_timer');
    if (!timerElement) return;

    const timerKey = 'bs_custom_bs_sale_timer_end';
    const startDuration = 18 * 3600 + 3 * 60 + 23; // 18h 3m 23s
    const now = Date.now();
    let savedEnd = null;
    try { savedEnd = localStorage.getItem(timerKey); } catch (e) { /* private mode */ }
    const endTime = savedEnd ? parseInt(savedEnd, 10) : now + startDuration * 1000;
    if (!savedEnd) {
      try { localStorage.setItem(timerKey, String(endTime)); } catch (e) { /* private mode */ }
    }

    function tick() {
      const remaining = Math.floor((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        timerElement.textContent = '00:00:00';
        try { localStorage.removeItem(timerKey); } catch (e) {}
        clearInterval(intervalId);
        return;
      }
      const hrs = String(Math.floor(remaining / 3600)).padStart(2, '0');
      const mins = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
      const secs = String(remaining % 60).padStart(2, '0');
      timerElement.textContent = `${hrs}:${mins}:${secs}`;
    }

    tick(); // paint immediately so first second isn't blank
    const intervalId = setInterval(tick, 1000);
  }

  // -------- delegated click handlers on document -----------------------------
  // Replaces 2 per-element querySelectorAll().forEach(addEventListener) blocks.
  // Document-level listener fires on every click but matches() is O(1) and we
  // early-return if no relevant ancestor.
  function attachDelegatedClicks() {
    document.addEventListener('click', (e) => {
      // Accordion (PDP): toggle aria-expanded on .accordion-item button group
      const accordionBtn = e.target.closest('.accordion-item button');
      if (accordionBtn) {
        e.preventDefault();
        const allButtons = document.querySelectorAll('.accordion-item button');
        allButtons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
        accordionBtn.setAttribute('aria-expanded', 'true');
        return;
      }
      // Additional-info collapsible: tap an h2 inside .additional_information_bs
      // → toggle .active on its sibling p's + h2 inside the same root.
      const addInfoH2 = e.target.closest('.additional_information_bs h2');
      if (addInfoH2) {
        const root = addInfoH2.closest('.additional_information_bs');
        if (!root) return;
        root.querySelectorAll('p, h2').forEach((el) => el.classList.toggle('active'));
        return;
      }
    }, { passive: false });
  }

  // -------- sticky-cart scroll visibility ------------------------------------
  // Throttled via RAF + ticking flag. Element looked up once; if it's not on
  // this page, the scroll listener is never even attached.
  function attachStickyScroll() {
    const stickyBar = document.querySelector('.sticky-add-to-cart-bs');
    if (!stickyBar) return;
    let ticking = false;
    document.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        stickyBar.style.display = (scrollPosition >= documentHeight - 200) ? 'none' : 'block';
        ticking = false;
      });
    }, { passive: true });
  }

  // -------- main init --------------------------------------------------------
  function initializeScript() {
    initializeProductSlider();
    initCountdown();
    attachDelegatedClicks();
    attachStickyScroll();
  }

  safeIdleCallback(() => domReady(initializeScript));
})();
